import { useEffect, useRef, useState } from "react";
import {
  useLazyGetJiraRequiredFieldsQuery,
  useGetProjectStatusLabelsMutation,
} from "@/features/bughurd/api";
import type { JiraConfigItem } from "../types";

export function useJiraFields(
  jiraConfig: JiraConfigItem[],
  jiraProjects: any[],
) {
  const [combinedResponse, setCombinedResponse] = useState<any[]>([]);
  const [combinedStatus, setCombinedStatus] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [getRequiredFields] = useLazyGetJiraRequiredFieldsQuery();
  const [getProjectStatusLabels] = useGetProjectStatusLabelsMutation();

  const prevKeysRef = useRef<string[]>([]);
  const prevKeysStatusRef = useRef<string[]>([]);

  // Fetch required fields
  useEffect(() => {
    const keys = jiraConfig.map((config) => config.key);
    const prevKeys = prevKeysRef.current;
    const newOrRemovedKeys = keys?.filter((key) => !prevKeys.includes(key));

    setCombinedResponse((prev) =>
      prev.filter((item) => keys.includes(item.key)),
    );

    if (newOrRemovedKeys.length === 0) {
      prevKeysRef.current = keys;
      return;
    }

    const fetchData = async () => {
      if (keys.length === 0) return setCombinedResponse([]);
      setLoading(true);
      const allData: any[] = [];

      for (const key of newOrRemovedKeys) {
        const name =
          jiraProjects?.length > 0
            ? jiraProjects?.find((project) => project.key === key)?.name
            : "";
        try {
          const result = await getRequiredFields(key).unwrap();
          if (
            result &&
            result.length > 0 &&
            result?.status !== "NO_RECORD_FOUND"
          ) {
            const filteredResult = result?.filter(
              (item: any) =>
                item?.required === true &&
                item?.fieldKey !== "summary" &&
                item?.fieldKey !== "description" &&
                item?.fieldKey !== "assignees" &&
                item?.fieldKey !== "reporter" &&
                item?.fieldKey !== "priority" &&
                item?.fieldKey !== "status" &&
                item?.fieldKey !== "issuetype" &&
                item?.fieldKey !== "project" &&
                item?.fieldKey !== "projectKey" &&
                item?.fieldKey !== "type",
            );
            const issueTypes = result?.filter(
              (item: any) => item?.fieldKey === "issuetype",
            )?.allowedValues;

            allData.push({
              key,
              name,
              data: filteredResult,
              issueTypes,
            });
          }
        } catch (error) {
          console.error("Failed to fetch required fields for", key, error);
        }
      }

      setCombinedResponse((prev) => {
        const updatedData = prev?.filter((item) => keys.includes(item.key));
        return [
          ...updatedData,
          ...allData?.filter(
            (newItem) => !prev.some((item) => item.key === newItem.key),
          ),
        ];
      });

      setLoading(false);
    };

    fetchData();
    prevKeysRef.current = keys;
  }, [jiraConfig]);

  // Fetch status labels
  useEffect(() => {
    const keys = jiraConfig.map((config) => config.key);
    const prevKeys = prevKeysStatusRef.current;
    const newOrRemovedKeys = keys?.filter((key) => !prevKeys.includes(key));

    setCombinedStatus((prev) =>
      prev?.filter((item) => keys.includes(item.key)),
    );

    if (newOrRemovedKeys.length === 0) {
      prevKeysStatusRef.current = keys;
      return;
    }

    const fetchStatusLabels = async () => {
      if (keys.length === 0) return setCombinedStatus([]);
      setLoading(true);
      const allData: any[] = [];

      for (const key of newOrRemovedKeys) {
        const name =
          jiraProjects?.length > 0
            ? jiraProjects?.find((project) => project.key === key)?.name
            : "";
        try {
          const res = await getProjectStatusLabels({ keys: [key] }).unwrap();
          if (res && res?.statuses && res.statuses?.length > 0) {
            allData.push({ key, name, status: res?.statuses });
          }
        } catch (error) {
          console.error("Failed to fetch status labels for", key, error);
        }
      }

      setCombinedStatus((prev) => {
        const updatedStatus = prev?.filter((item) => keys.includes(item.key));
        return [
          ...updatedStatus,
          ...allData?.filter(
            (newItem) => !prev.some((item) => item.key === newItem.key),
          ),
        ];
      });

      setLoading(false);
    };

    fetchStatusLabels();
    prevKeysStatusRef.current = keys;
  }, [jiraConfig]);

  return { combinedResponse, combinedStatus, loading };
}
